import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { get, post, del } from '@/lib/apiClient';
import { API_URL } from '@/_config';

export interface SearchContext {
  query: string;
  source: string[];
  titles: string[];
  locations: string[];
  company: string[];
}

export interface RecentLead {
  id: string;
  name: string;
  company: string;
  title: string;
  industry: string;
  location: string;
  source: string;
  added: string;
  viewedAt: string;      // ISO timestamp
  searchContext?: SearchContext; // filters active when the lead was viewed
}

const QUERY_KEY = ['recently-viewed'];

export function useRecentlyViewed() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => get<{ items: RecentLead[] }>(API_URL.RECENTLY_VIEWED.LIST),
    staleTime: 30_000,
  });

  const recordMutation = useMutation({
    mutationFn: (lead: Omit<RecentLead, 'viewedAt'> & { searchContext?: SearchContext }) =>
      post(API_URL.RECENTLY_VIEWED.RECORD, {
        lead_provider_id: lead.id,
        lead_name:        lead.name,
        lead_company:     lead.company,
        lead_title:       lead.title,
        lead_industry:    lead.industry,
        lead_location:    lead.location,
        lead_source:      lead.source,
        lead_email:       (lead as any).email ?? null,
        search_context:   lead.searchContext ?? null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const clearMutation = useMutation({
    mutationFn: () => del(API_URL.RECENTLY_VIEWED.CLEAR),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  return {
    items: data?.data?.items ?? [],
    record: (lead: Omit<RecentLead, 'viewedAt'> & { searchContext?: SearchContext }) =>
      recordMutation.mutate(lead),
    clear: () => clearMutation.mutate(),
  };
}
